/**
 * Customer order-bill tray identity.
 * Must match server/services/orderBillNotification.js.
 * One stable tag replaces the previous bill. Older per-order notifications
 * are cancelled by id when a new order is placed.
 */
export const ORDER_BILL_TAG = 'qahwahub-order-bill';
export const ORDER_BILL_CHANNEL_ID = 'order-bill';
export const ORDER_BILL_COLOR = '#FBBF24';
export const ORDER_BILL_TYPE = 'order_bill';

export interface TrayNotification {
  id: string;
  tag?: string;
  title?: string;
  body?: string;
  data?: Record<string, unknown> | null;
}

export interface KeepOrderBill {
  orderId?: string | null;
  orderNumber?: string | null;
}

function readData(notification: TrayNotification, key: string): string {
  const value = notification.data ? notification.data[key] : undefined;
  if (value === undefined || value === null) return '';
  return String(value);
}

function orderNumberInText(text: string): string {
  const match = String(text || '').match(/ORD-\d+/i);
  return match ? match[0].toUpperCase() : '';
}

function notificationOrderNumber(notification: TrayNotification): string {
  return (
    readData(notification, 'order_number').toUpperCase() ||
    orderNumberInText(notification.title || '') ||
    orderNumberInText(notification.body || '')
  );
}

function notificationTag(notification: TrayNotification): string {
  return String(notification.tag || readData(notification, 'notification_tag') || '');
}

/** Order bill and older per-order status pushes. Cafe offers are left alone. */
export function isOrderTrayNotification(notification: TrayNotification): boolean {
  const type = readData(notification, 'type');
  if (notificationTag(notification) === ORDER_BILL_TAG) return true;
  if (type === ORDER_BILL_TYPE || type === 'order_update') return true;
  if (readData(notification, 'order_id')) return true;
  return /^Order\b/i.test(String(notification.title || ''));
}

function belongsToKeptOrder(notification: TrayNotification, keep: KeepOrderBill): boolean {
  const keepId = String(keep.orderId || '');
  const keepNumber = String(keep.orderNumber || '').toUpperCase();
  const orderId = readData(notification, 'order_id');
  const orderNumber = notificationOrderNumber(notification);
  if (keepId && orderId && orderId === keepId) return true;
  if (keepNumber && orderNumber && orderNumber === keepNumber) return true;
  if (notificationTag(notification) === ORDER_BILL_TAG && !orderId && !orderNumber) return true;
  return false;
}

/**
 * Tray items that belong to an older order and should be cancelled.
 * A notification is removed only when its order id or order number
 * positively differs from the order we are keeping.
 */
export function selectStaleOrderBills(
  notifications: TrayNotification[],
  keep: KeepOrderBill
): TrayNotification[] {
  const keepId = String(keep.orderId || '');
  const keepNumber = String(keep.orderNumber || '').toUpperCase();
  return (notifications || []).filter((notification) => {
    if (!notification || !isOrderTrayNotification(notification)) return false;
    if (belongsToKeptOrder(notification, keep)) return false;
    const orderId = readData(notification, 'order_id');
    const orderNumber = notificationOrderNumber(notification);
    if (keepId && orderId && orderId !== keepId) return true;
    if (keepNumber && orderNumber && orderNumber !== keepNumber) return true;
    return false;
  });
}

/** Cancel older order bills on this device. No-op in the browser. */
export async function dismissStaleOrderBills(keep: KeepOrderBill): Promise<void> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return;
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const delivered = await PushNotifications.getDeliveredNotifications();
    const stale = selectStaleOrderBills(delivered?.notifications || [], keep);
    if (!stale.length) return;
    await PushNotifications.removeDeliveredNotifications({
      notifications: stale.map((notification) => ({
        id: notification.id,
        tag: notification.tag,
        title: notification.title,
        body: notification.body,
        data: notification.data || {},
      })),
    });
  } catch (error) {
    console.warn('Could not clear the previous order bill notification', error);
  }
}
